import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { SupportUsersModel } from '@models/support-users.model';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { SupportUsersActions } from '@states/support-users/support-users.actions';

export interface SupportUserState extends EntityState<SupportUsersModel.SupportUserDocument> {
  page: number;
  perPage: number;
  isLoading: boolean;
  isLastPage: boolean;
  filters: SupportUsersModel.Filters;
}

export const adapter: EntityAdapter<SupportUsersModel.SupportUserDocument> =
  createEntityAdapter<SupportUsersModel.SupportUserDocument>({
    selectId: (user: SupportUsersModel.SupportUserDocument) => user._id,
  });

export const initialState: SupportUserState = adapter.getInitialState({
  page: 1,
  perPage: 20,
  isLoading: false,
  isLastPage: false,
  filters: {
    query: '',
    roles: [],
  },
});

export const supportUsersReducer = createReducer(
  initialState,
  on(SupportUsersActions.resetToInitialState, () => ({
    ...initialState,
  })),
  on(SupportUsersActions.setFilters, (state, { filters }) => ({
    ...state,
    filters,
    page: 1,
  })),
  on(SupportUsersActions.loadSupportUsers, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SupportUsersActions.loadSupportUsersSuccess, (state, { users }) =>
    adapter.setAll(users, {
      ...state,
      isLoading: false,
      isLastPage: users.length < state.perPage,
      page: state.page + 1,
    }),
  ),
  on(SupportUsersActions.loadSupportUsersFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SupportUsersActions.resetList, (state) => {
    return adapter.removeAll({
      ...state,
      isLastPage: initialState.isLastPage,
      page: initialState.page,
    });
  }),
  on(SupportUsersActions.setIsLoading, (state, { isLoading }) => ({
    ...state,
    isLoading,
  })),
);

export const supportUsersFeature = createFeature({
  name: 'supportUsers',
  reducer: supportUsersReducer,
  extraSelectors: ({ selectSupportUsersState }) => {
    const {
      selectAll: selectAllSupportUsers,
      selectEntities: selectSupportUserEntities,
      selectIds: selectSupportUserIds,
      selectTotal: selectSupportUsersTotal,
    } = adapter.getSelectors(selectSupportUsersState);

    const selectIsLoading = createSelector(
      selectSupportUsersState,
      (state) => state.isLoading,
    );

    const selectPage = createSelector(
      selectSupportUsersState,
      (state) => state.page,
    );

    const selectPerPage = createSelector(
      selectSupportUsersState,
      (state) => state.perPage,
    );

    const selectIsLastPage = createSelector(
      selectSupportUsersState,
      (state) => state.isLastPage,
    );

    const selectFilters = createSelector(
      selectSupportUsersState,
      (state) => state.filters,
    );

    const selectFiltersQuery = createSelector(
      selectFilters,
      (filters) => filters.query,
    );

    const selectFiltersRoles = createSelector(
      selectFilters,
      (filters) => filters.roles,
    );

    const selectHasUsers = createSelector(
      selectSupportUsersTotal,
      (total) => total > 0,
    );

    const selectSupportUserById = (id: string) =>
      createSelector(selectSupportUserEntities, (entities) => entities[id]);

    return {
      selectAllSupportUsers,
      selectSupportUserEntities,
      selectSupportUserIds,
      selectSupportUsersTotal,
      selectIsLoading,
      selectPage,
      selectPerPage,
      selectIsLastPage,
      selectFilters,
      selectFiltersQuery,
      selectFiltersRoles,
      selectHasUsers,
      selectSupportUserById,
    };
  },
});